import * as React from 'react';
import { Image } from 'expo-image';
import { Image as RNImage, View } from 'react-native';
import { getPosterImageFrameStyle } from '@/features/events/shared/posterFocal';
import type { DraftPoster } from './types';

export function PosterFocalImage({
  poster,
  borderRadius,
}: {
  poster: DraftPoster;
  borderRadius: number;
}) {
  const [layout, setLayout] = React.useState({ width: 0, height: 0 });
  const [resolvedSize, setResolvedSize] = React.useState<{ width: number; height: number } | null>(
    null
  );
  const sourceURI = poster.publicURL ?? poster.uri;

  // When the poster has no stored dimensions (server-loaded posters never have them),
  // resolve them lazily. The image is already cached at this point so getSize is fast.
  React.useEffect(() => {
    if (poster.width && poster.height) return;
    if (!sourceURI) return;
    RNImage.getSize(
      sourceURI,
      (width, height) => setResolvedSize({ width, height }),
      () => {}
    ); // silent fail — fallback cover render stays
  }, [sourceURI, poster.width, poster.height]);

  const imageWidth = poster.width ?? resolvedSize?.width ?? null;
  const imageHeight = poster.height ?? resolvedSize?.height ?? null;

  const imageFrameStyle = getPosterImageFrameStyle(
    imageWidth,
    imageHeight,
    layout.width,
    layout.height,
    poster.focalPointX,
    poster.focalPointY,
    poster.focalScale ?? 1
  );

  return (
    <View
      onLayout={(event) =>
        setLayout({
          width: event.nativeEvent.layout.width,
          height: event.nativeEvent.layout.height,
        })
      }
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius,
        backgroundColor: 'rgba(255,255,255,0.04)',
      }}>
      {layout.width > 0 && layout.height > 0 && imageFrameStyle ? (
        <Image
          key={`${sourceURI}-${layout.width}-${layout.height}`}
          source={{ uri: sourceURI }}
          contentFit="fill"
          cachePolicy="memory-disk"
          style={[
            {
              position: 'absolute',
            },
            imageFrameStyle,
          ]}
        />
      ) : (
        <Image
          key={`${sourceURI}-${layout.width}-${layout.height}`}
          source={{ uri: sourceURI }}
          contentFit="cover"
          cachePolicy="memory-disk"
          transition={200}
          style={{ width: '100%', height: '100%' }}
        />
      )}
    </View>
  );
}
